import React, { useMemo, useState } from "react";
import Slider from "react-slick";
import { galleryPhotos, galleryVideos } from "../data/galleryManifest";
import { mediaAbsoluteUrl } from "../lib/apiBase";

function slickSettings(count) {
  return {
    dots: count > 1,
    infinite: count > 1,
    speed: 500,
    slidesToShow: Math.min(3, Math.max(1, count)),
    slidesToScroll: 1,
    arrows: false,
    autoplay: count > 1,
    autoplaySpeed: 5200,
    responsive: [
      {
        breakpoint: 900,
        settings: { slidesToShow: Math.min(2, Math.max(1, count)) },
      },
      { breakpoint: 600, settings: { slidesToShow: 1 } },
    ],
  };
}

export default function GallerySection() {
  const photos = galleryPhotos;
  const videos = galleryVideos;
  const [tab, setTab] = useState("photos");

  const photoSettings = useMemo(
    () => slickSettings(photos.length || 1),
    [photos.length],
  );
  const videoSettings = useMemo(
    () => slickSettings(videos.length || 1),
    [videos.length],
  );

  return (
    <section id="gallery" className="sec gal-sec">
      <div className="gal-hdr rv">
        <div>
          <div className="kicker k-gold">Media</div>
          <h2 className="title lt">
            Laboratory &amp; plant
            <br />
            <em>gallery</em>
          </h2>
        </div>
        <p className="gal-sub">
          Auralux gallery highlights the science, systems, and finished results
          behind our polymer solutions. Browse laboratory imagery and production
          clips designed to show how color, function, and sustainability come
          together in every material application.
        </p>
      </div>

      <div className="gal-tabs rv">
        <button
          type="button"
          className={tab === "photos" ? "gal-tab on" : "gal-tab"}
          onClick={() => setTab("photos")}
        >
          Photos
        </button>
        <button
          type="button"
          className={tab === "videos" ? "gal-tab on" : "gal-tab"}
          onClick={() => setTab("videos")}
        >
          Videos
        </button>
      </div>

      {tab === "photos" && (
        <div className="gal-slider">
          {photos.length === 0 ? (
            <p className="gal-note">
              No photos listed. Add files under <code>public/gallery</code> and
              rows in <code>galleryManifest.js</code>.
            </p>
          ) : (
            <Slider
              {...photoSettings}
              key={`ph-${photos.map((p) => p.id).join("|")}`}
            >
              {photos.map((p) => (
                <div key={p.id} className="gal-slide">
                  <figure className="gal-card">
                    <img
                      src={mediaAbsoluteUrl(p.url)}
                      alt={p.title}
                      decoding="async"
                    />
                    <figcaption>{p.title}</figcaption>
                  </figure>
                </div>
              ))}
            </Slider>
          )}
        </div>
      )}

      {tab === "videos" && (
        <div className="gal-slider">
          {videos.length === 0 ? (
            <p className="gal-note">
              No videos listed. Add a file under <code>public/gallery</code> and
              an entry in <code>galleryManifest.js</code> (and{" "}
              <code>server/data/gallery.json</code>).
            </p>
          ) : (
            <Slider
              {...videoSettings}
              key={`vd-${videos.map((v) => v.id).join("|")}`}
            >
              {videos.map((v) => (
                <div key={v.id} className="gal-slide">
                  <figure className="gal-card gal-video">
                    <video
                      src={mediaAbsoluteUrl(v.url)}
                      controls
                      playsInline
                      preload="metadata"
                      title={v.title}
                    />
                    <figcaption>{v.title}</figcaption>
                  </figure>
                </div>
              ))}
            </Slider>
          )}
        </div>
      )}
    </section>
  );
}
